import React, { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip } from "@mui/material";
import { DownlineIcon } from "../../../../../Components/Icons/downlineIcon";
import { ResendIcon } from "../../../../../Components/Icons/resendIcon";
import {
    AGENT_ACTION_BUTTON_TOOLTIPS,
    AGENT_ACTION_ICON_COLORS,
    AGENTS_EDIT_ROUTE,
    EDIT_AGENT_QUERY_PARAM,
} from "../../../agents.constants";
import { IAgentsDetailsResp } from "../../../agents.model";
import { useAgentsStore } from "../../../store/agents.store";
import styles from "./agentRowActions.module.css";
import { ReactComponent as ViewDetails } from "../../../../../Assets/viewDetails.svg";
import { UserContext, UserPayload } from "../../../../../Context/userContext";

interface AgentRowActionsProps {
    record: IAgentsDetailsResp;
    onCreateDownline: (userId: number) => void;
}

export const AgentRowActions = ({ record, onCreateDownline }: AgentRowActionsProps) => {
    const { setShowLoader } = useContext(UserContext) as UserPayload;

    const navigate = useNavigate();
    const resendInviteToAgent = useAgentsStore((state) => state.resendInviteToAgent);

    const handleEdit = useCallback(() => {
        navigate(`${AGENTS_EDIT_ROUTE}?${EDIT_AGENT_QUERY_PARAM}=${record.userId}`);
    }, [navigate, record.userId]);

    const handleCreateDownline = useCallback(() => {
        onCreateDownline(record.userId);
    }, [onCreateDownline, record.userId]);

    const handleResendInvite = useCallback(() => {
        if (!record.email) return;
        resendInviteToAgent(record.email, { setShowLoader });
    }, [record.email, resendInviteToAgent]);

    return (
        <Box className={styles.editAgentIconDiv}>
            <Tooltip
                title={
                    record.flagCreateDownlineAvailable
                        ? AGENT_ACTION_BUTTON_TOOLTIPS.CREATE_DOWNLINE
                        : AGENT_ACTION_BUTTON_TOOLTIPS.CREATE_DOWNLINE_DISABLED
                }
                arrow
            >
                <span>
                    <IconButton
                        aria-label="create downline"
                        onClick={handleCreateDownline}
                        size="small"
                        disabled={!record.flagCreateDownlineAvailable}
                    >
                        <DownlineIcon
                            width={18}
                            height={16}
                            className={styles.actionIcon}
                            fillColor={
                                record.flagCreateDownlineAvailable
                                    ? AGENT_ACTION_ICON_COLORS.ENABLED
                                    : AGENT_ACTION_ICON_COLORS.DISABLED
                            }
                        />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Edit Agent" arrow>
                <IconButton aria-label="edit agent" onClick={handleEdit} size="small">
                    <ViewDetails width={18} height={18} className={styles.editAgentIcon} />
                </IconButton>
            </Tooltip>
            {record.flagResendInviteAvailable && (
                <Tooltip title={AGENT_ACTION_BUTTON_TOOLTIPS.RESEND_INVITE} arrow>
                    <span>
                        <IconButton
                            aria-label="resend invite"
                            onClick={handleResendInvite}
                            size="small"
                            disabled={!record.email}
                        >
                            <ResendIcon
                                width={18}
                                height={14}
                                className={styles.actionIcon}
                                fillColor={
                                    record.email
                                        ? AGENT_ACTION_ICON_COLORS.ENABLED
                                        : AGENT_ACTION_ICON_COLORS.DISABLED
                                }
                            />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Box>
    );
};
